import { SoulClipItem } from '../components/studio/studioUtils';

let isFetchingSharedClips = false;

export interface ShareQueryParams {
  mode?: 'my_shares' | 'direct' | 'following' | 'community';
  user?: string;
  friend?: string;
  sortBy?: 'newest' | 'most_viewed' | 'most_liked' | 'completion';
  search?: string;
}

export const SAMPLE_SHARED_CLIPS: SoulClipItem[] = [];

export async function fetchSharedClips(params: ShareQueryParams): Promise<SoulClipItem[]> {
  if (typeof window === 'undefined') {
    return [];
  }

  // Prevent querying user-specific feeds without an active user handle
  if ((params.mode === 'my_shares' || params.mode === 'direct' || params.mode === 'following') && !params.user?.trim()) {
    return [];
  }

  if (isFetchingSharedClips) {
    return [];
  }
  isFetchingSharedClips = true;

  try {
    const searchParams = new URLSearchParams();
    if (params.mode) searchParams.set('mode', params.mode);
    if (params.user) searchParams.set('user', params.user.trim());
    if (params.friend) searchParams.set('friend', params.friend.trim());
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.search) searchParams.set('search', params.search.trim());

    const res = await fetch(`/api/share/clips?${searchParams.toString()}`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && Array.isArray(data.clips)) {
        return data.clips;
      }
    }
  } catch (err) {
    console.warn('[shareService] fetchSharedClips notice:', err);
  } finally {
    isFetchingSharedClips = false;
  }

  return [];
}

function filterAndSortClips(clips: SoulClipItem[], params: ShareQueryParams): SoulClipItem[] {
  let list = [...clips];

  if (params.mode === 'my_shares') {
    const u = (params.user || 'Dan').toLowerCase();
    list = list.filter((c) => (c.creatorName || c.userName || '').toLowerCase() === u);
  } else if (params.mode === 'direct') {
    const u = (params.user || 'Dan').toLowerCase();
    list = list.filter((c) => 
      c.shareVisibility === 'direct' && 
      (c.sharedToUsers?.some((name) => name.toLowerCase() === u) || (c.creatorName || '').toLowerCase() === u)
    );
  } else {
    // community
    list = list.filter((c) => c.shareVisibility === 'public');
  }

  if (params.friend) {
    const f = params.friend.toLowerCase();
    list = list.filter((c) => (c.creatorName || '').toLowerCase() === f || (c.userName || '').toLowerCase() === f);
  }

  if (params.search) {
    const term = params.search.toLowerCase();
    list = list.filter((c) => 
      c.title.toLowerCase().includes(term) ||
      (c.creatorName || '').toLowerCase().includes(term) ||
      (c.shareMessage || '').toLowerCase().includes(term)
    );
  }

  // Sort criteria
  if (params.sortBy === 'most_viewed') {
    list.sort((a, b) => (b.viewsCount || 0) - (a.viewsCount || 0));
  } else if (params.sortBy === 'most_liked') {
    list.sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
  } else if (params.sortBy === 'completion') {
    list.sort((a, b) => {
      const ratioA = (a.dubbedLinesCount || 0) / Math.max(1, a.totalLinesCount || 1);
      const ratioB = (b.dubbedLinesCount || 0) / Math.max(1, b.totalLinesCount || 1);
      return ratioB - ratioA;
    });
  } else {
    // newest
    list.sort((a, b) => (b.sharedAt || b.createdAt || 0) - (a.sharedAt || a.createdAt || 0));
  }

  return list;
}

let isPublishingSnapshot = false;

export interface PublishSnapshotParams {
  clipId: string;
  creatorName: string;
  shareVisibility: 'public' | 'direct' | 'external';
  sharedToUsers?: string[];
  shareMessage?: string;
  title?: string;
  videoUrl?: string;
  audioUrl?: string;
  posterUrl?: string;
  duration?: number;
  trimStart?: number;
  trimEnd?: number;
  anchorTime?: number;
  dialogueLines?: any[];
  takes?: any[];
  isEditable?: boolean;
  existingSharedClipId?: string;
}

export async function publishClipSnapshot(params: PublishSnapshotParams): Promise<{ success: boolean; clip?: SoulClipItem; error?: string }> {
  if (isPublishingSnapshot) {
    return { success: false, error: 'Publishing already in progress' };
  }
  isPublishingSnapshot = true;
  try {
    const res = await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...params,
        action: 'publish_snapshot'
      })
    });
    const data = await res.json() as any;
    return data;
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  } finally {
    isPublishingSnapshot = false;
  }
}

export async function updateClipShare(config: {
  clipId: string;
  isShared: boolean;
  shareVisibility: 'public' | 'direct' | 'external' | 'private';
  sharedToUsers?: string[];
  shareMessage?: string;
  isEditable?: boolean;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...config,
        action: config.isShared ? 'share' : 'unshare'
      })
    });
    const data = await res.json() as any;
    return { success: Boolean(data.success), error: data.error };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

export async function toggleClipLike(clipId: string): Promise<{ success: boolean; likesCount?: number }> {
  try {
    const res = await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, action: 'like' })
    });
    const data = await res.json() as any;
    return { success: Boolean(data.success), likesCount: data.likesCount };
  } catch {
    return { success: true };
  }
}

export async function recordClipView(clipId: string): Promise<void> {
  try {
    await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, action: 'view' })
    });
  } catch (_) {}
}

export async function saveClipToVault(clipId: string, username: string): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, username, action: 'save_to_vault' })
    });
    const data = await res.json() as any;
    return { success: Boolean(data.success), error: data.error };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

export async function deleteSharedClip(
  clipId: string,
  requestingUser: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch('/api/share/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, creatorName: requestingUser, action: 'delete' })
    });
    const data = await res.json() as any;
    return { success: Boolean(data.success), error: data.error };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}
